import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import styles from './BillViewerPopUp.module.css'
import { CUSTOMER_DETAILED_INVOICE, INVOICE_LIST_BY_DATE_AND_TIME, INVOICE_LIST_BY_DATE, DELETE_BILL_URL } from '../Api/ApiConstants.js';
import TransactionModal from './TransactionModal.js';
import PrintBillModal from '../print/PrintBillModal.js';


const BillViewerPopUp = ({ onClose }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [bills, setBills] = useState([]);
  const [error, setError] = useState('');
  const [selectedBill, setSelectedBill] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [activeFilter, setActiveFilter] = useState('Today');
  const [sortOrder, setSortOrder] = useState('Recent');
  const pdfIframeRef = useRef(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedBillNo, setSelectedBillNo] = useState(null);
  const [totalAmount, setTotalAmount] = useState(null);
  const [quarters, setQuarter] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState('');
  

   const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');

    return {
        user: user ? JSON.parse(user) : null,
        token: token || null
    };
 };
  
  // Time range filter states
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [startTimeHour, setStartTimeHour] = useState('');
  const [startTimeMinute, setStartTimeMinute] = useState('00');
  const [endTimeHour, setEndTimeHour] = useState('');
  const [endTimeMinute, setEndTimeMinute] = useState('00');
  const [startTimePeriod, setStartTimePeriod] = useState('AM');
  const [endTimePeriod, setEndTimePeriod] = useState('PM');
  const [isTimeFilterActive, setIsTimeFilterActive] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [billInfo, setBillInfo] = useState(null);


  // Convert 12-hour time to 24-hour format
  const convertTo24Hour = (hour, minute, period) => {
    if (!hour || !minute) return null;
    
    let hour24 = parseInt(hour);
    
    if (period === 'PM' && hour24 !== 12) {
      hour24 += 12;
    } else if (period === 'AM' && hour24 === 12) {
      hour24 = 0;
    }
    
    return `${hour24.toString().padStart(2, '0')}:${minute}`;
  };

  const fetchBills = async () => {
    if (activeFilter === 'Custom Date') {
      if (!startDate || !endDate) {
        return;
      }
    }
    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;

      const response = await axios.get(`${INVOICE_LIST_BY_DATE}`, {
        params: { startDate, endDate, storeId },
      });
      
      const sortedBills = [...response.data].sort((a, b) => {
        const billNoA = parseInt(a.billNo);
        const billNoB = parseInt(b.billNo);
        return sortOrder === 'Recent' ? billNoB - billNoA : billNoA - billNoB;
      });
      
      setBills(sortedBills);
      setError('');
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError('No bills found for the selected date range.');
      } else {
        setError('An error occurred while fetching bills.');
      }
    }
  };

  const fetchBillsWithTimeRange = async () => {
    if (!startDate || !endDate || !startTimeHour) {
      setError('Please select date range and start time');
      return;
    }

    try {
      const user = JSON.parse(sessionStorage.getItem('user'));
      const storeId = user?.storeId;

      const startTime24 = convertTo24Hour(startTimeHour, startTimeMinute, startTimePeriod);
      const endTime24 = endTimeHour ? convertTo24Hour(endTimeHour, endTimeMinute, endTimePeriod) : null;

      const response = await axios.get(`${INVOICE_LIST_BY_DATE_AND_TIME}`, {
        params: {
          startDate,
          endDate,
          startTime: startTime24,
          endTime: endTime24,
          storeId
        },
      });
      
      const sortedBills = [...response.data].sort((a, b) => {
        const billNoA = parseInt(a.billNo);
        const billNoB = parseInt(b.billNo);
        return sortOrder === 'Recent' ? billNoB - billNoA : billNoA - billNoB;
      });
      
      setBills(sortedBills);
      setError('');
      setIsTimeFilterActive(true);
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError('No bills found for the selected date and time range.');
      } else {
        setError('An error occurred while fetching bills.');
      }
    }
  };

  const handleQuarterChange = (quarterKey) => {
    if (!quarters || !quarterKey) {
      setSelectedQuarter('');
      return;
    }

    const selected = quarters[quarterKey];
    if (selected) {
      setStartDate(selected.start);
      setEndDate(selected.end);
      setActiveFilter('Quarter');
      setSelectedQuarter(quarterKey);
    }
  };

  const getIndianFYQuarters = () => {
    const today = new Date();
    const fyStartYear = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();

    return {
      Q1: {
        label: `Q1: 1 Apr ${fyStartYear} - 30 Jun ${fyStartYear}`,
        start: `${fyStartYear}-04-01`,
        end: `${fyStartYear}-06-30`,
      },
      Q2: {
        label: `Q2: 1 Jul ${fyStartYear} - 30 Sep ${fyStartYear}`,
        start: `${fyStartYear}-07-01`,
        end: `${fyStartYear}-09-30`,
      },
      Q3: {
        label: `Q3: 1 Oct ${fyStartYear} - 31 Dec ${fyStartYear}`,
        start: `${fyStartYear}-10-01`,
        end: `${fyStartYear}-12-31`,
      },
      Q4: {
        label: `Q4: 1 Jan ${fyStartYear + 1} - 31 Mar ${fyStartYear + 1}`,
        start: `${fyStartYear + 1}-01-01`,
        end: `${fyStartYear + 1}-03-31`,
      },
    };
  };

  useEffect(() => {
    setBills([]);
    if (startDate && endDate && !isTimeFilterActive) {
      fetchBills();
    }
  }, [startDate, endDate, sortOrder]);

  useEffect(() => {
    const quarters = getIndianFYQuarters();
    setQuarter(quarters);
    handleDateFilter('Today');
  }, []); 

  const handleDateFilter = async (filter) => {
    const today = new Date();
    let calculatedStartDate = '';
    let calculatedEndDate = format(today, 'yyyy-MM-dd');
  
    setSelectedQuarter('');
    
    switch (filter) {
      case 'Today':
        calculatedStartDate = calculatedEndDate;
        break;
  
      case 'This Week':
        const startOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
        calculatedStartDate = format(startOfWeek, 'yyyy-MM-dd');
        break;
  
      case 'This Month':
        calculatedStartDate = format(new Date(today.getFullYear(), today.getMonth(), 1), 'yyyy-MM-dd');
        calculatedEndDate = format(
          new Date(today.getFullYear(), today.getMonth() + 1, 0),
          'yyyy-MM-dd'
        );
        break;
  
      case 'Previous Month':
        const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        calculatedStartDate = format(prevMonthStart, 'yyyy-MM-dd');
        calculatedEndDate = format(prevMonthEnd, 'yyyy-MM-dd');
        break;
  
      case 'This Quarter':
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        const quarterEndMonth = quarterStartMonth + 2;
        calculatedStartDate = format(new Date(today.getFullYear(), quarterStartMonth, 1), 'yyyy-MM-dd');
        calculatedEndDate = format(
          new Date(today.getFullYear(), quarterEndMonth + 1, 0),
          'yyyy-MM-dd'
        );
        break;
  
      case 'This FY':
        const currentFYStartMonth = today.getMonth() >= 3 ? 3 : -9;
        calculatedStartDate = format(new Date(today.getFullYear(), currentFYStartMonth, 1), 'yyyy-MM-dd');
        const endYear = currentFYStartMonth === -9 ? today.getFullYear() : today.getFullYear() + 1;
        calculatedEndDate = format(new Date(endYear, 2, 31), 'yyyy-MM-dd');
        break;

      case 'Custom Date':
        setStartDate('');
        setEndDate('');
        break;
      default:
        return;
    }
  
    setStartDate(calculatedStartDate);
    setEndDate(calculatedEndDate);
    setActiveFilter(filter);
  };
  
  const handleSortOrderChange = (order) => {
    setSortOrder(order);
  };

  const handleViewDetails = (bill) => {
    setSelectedBill(bill);
  };

  const closePopup = () => {
    setSelectedBill(null);
  };

  const handlePrintBill = async (billNo) => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'));
    const storeId = user?.storeId;
    
    const storeConfig = JSON.parse(sessionStorage.getItem("storeConfig") || "{}");
    const printType = (storeConfig.printType || "PDF").trim().toUpperCase();

    if (printType === "THERMAL") {
      // For thermal printing, fetch bill data and show modal
      const response = await axios.get(`${CUSTOMER_DETAILED_INVOICE}`, {
        params: { billNo, storeId, printType }
      });
      
      // Set bill info and show modal
      setBillInfo(response.data);
      setShowPrintModal(true);
      
    } else {
      // For PDF printing, fetch and print
      const response = await axios.get(`${CUSTOMER_DETAILED_INVOICE}`, {
        params: { billNo, storeId, printType },
        responseType: 'arraybuffer',
      });

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Create a hidden iframe for printing
      const printIframe = document.createElement('iframe');
      printIframe.style.position = 'absolute';
      printIframe.style.width = '0';
      printIframe.style.height = '0';
      printIframe.style.border = 'none';
      printIframe.style.visibility = 'hidden';
      
      document.body.appendChild(printIframe);
      
      printIframe.onload = function() {
        try {
          // Wait for PDF to fully render
          setTimeout(() => {
            printIframe.contentWindow.focus();
            printIframe.contentWindow.print();
            
            // Listen for browser events to detect when print dialog is closed
            const cleanupResources = () => {
              setTimeout(() => {
                if (document.body.contains(printIframe)) {
                  document.body.removeChild(printIframe);
                }
                URL.revokeObjectURL(pdfUrl);
              }, 100);
            };

            // Listen for focus return (print dialog closed)
            window.addEventListener('focus', cleanupResources, { once: true });
            
            // Also cleanup after a long timeout as fallback (5 minutes)
            setTimeout(cleanupResources, 300000);
          }, 500);
        } catch (err) {
          console.error('Error printing:', err);
          // Fallback: open in new window
          window.open(pdfUrl, '_blank');
          setTimeout(() => {
            if (document.body.contains(printIframe)) {
              document.body.removeChild(printIframe);
            }
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        }
      };
      
      printIframe.onerror = function() {
        console.error('Error loading PDF in iframe');
        // Fallback: open in new window
        window.open(pdfUrl, '_blank');
        if (document.body.contains(printIframe)) {
          document.body.removeChild(printIframe);
        }
        URL.revokeObjectURL(pdfUrl);
      };
      
      printIframe.src = pdfUrl;
    }
  } catch (error) {
    console.error('Error processing bill:', error);
    setError('An error occurred while processing the bill.');
  }
};

  const printPdf = () => {
    if (pdfIframeRef.current) {
      pdfIframeRef.current.contentWindow.print();
    }
  };

  const closePdfModal = () => {
    setPdfData(null);
  };

  const openModal = (billNo, totalAmount) => {
    setSelectedBillNo(billNo);
    setTotalAmount(totalAmount);
    setShowTransactionModal(true);
  };

  const closeModal = () => {
    setShowTransactionModal(false);
    setTotalAmount(null);
    setSelectedBillNo(null);
  };

 const handleDeleteBill = async (billNo) => {
    try {
      const { user, token } = getUserData();
      
      if (!user || !token) {
        alert('User not authenticated');
        return;
      }

      const requestBody = {
        user,
        token,
        billNo: billNo
      };

      const response = await axios.post(
        `${DELETE_BILL_URL}`,
        requestBody
      );
      
      alert(response.data.message || "Unable to delete bill");
    } catch (error) {
      console.error('Error canceling bill:', error);
      alert(error.response?.data?.message || 'An error occurred while canceling the bill.');
    } finally {
      if (isTimeFilterActive) {
        fetchBillsWithTimeRange();
      } else {
        fetchBills();
      }
    }
};

  
  
  const isToday = (dateString) => {
    const today = new Date().toISOString().split('T')[0];
    return dateString === today;
  };

  const openTimeModal = () => {
    setShowTimeModal(true);
  };

  const closeTimeModal = () => {
    setShowTimeModal(false);
  };

  const applyTimeFilter = () => {
    if (!startTimeHour) {
      setError('Please select at least a start time');
      return;
    }
    setShowTimeModal(false);
    fetchBillsWithTimeRange();
  };

  const removeTimeFilter = () => {
    setStartTimeHour('');
    setStartTimeMinute('00');
    setEndTimeHour('');
    setEndTimeMinute('00');
    setStartTimePeriod('AM');
    setEndTimePeriod('PM');
    setIsTimeFilterActive(false);
    fetchBills();
  };

  // Generate hour options (1-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 1;
    return hour.toString().padStart(2, '0');
  });

  // Generate minute options (00-59)
  const minuteOptions = Array.from({ length: 60 }, (_, i) => {
    return i.toString().padStart(2, '0');
  });

  const getTimeDisplayString = () => {
    if (!startTimeHour) return '';
    const startStr = `${startTimeHour}:${startTimeMinute} ${startTimePeriod}`;
    const endStr = endTimeHour ? `${endTimeHour}:${endTimeMinute} ${endTimePeriod}` : 'onwards';
    return `${startStr} - ${endStr}`;
  };

  return (
    <div className={styles.billViewerPopup}>
      <div className={styles.billViewerPopupContent}>
        <button className={styles.billViewerClosePopup} onClick={onClose}>
          Close
        </button>
        
        <div className={styles.billViewer}>
          <h1>Recent Bills</h1>
          
          <div className={styles.billViewerTopContainer}>
            <div className={styles.billViewerFilters}>
              {['Today', 'This Week', 'This Month', 'Previous Month', 'This FY', 'Custom Date'].map(
                (filter) => (
                  <button
                    key={filter}
                    className={`${styles.billViewerFilterButton} ${activeFilter === filter ? styles.active : ''}`}
                    onClick={() => handleDateFilter(filter)}
                  >
                    {filter}
                  </button>
                )
              )}

              <button
                className={`${styles.billViewerFilterButton} ${styles.timeRangeButton} ${isTimeFilterActive ? styles.active : ''}`}
                onClick={openTimeModal}
              >
                Time Range
              </button>

              {quarters && (
                <div className={styles.quarterDropdown}>
                  <select 
                    value={selectedQuarter} 
                    onChange={(e) => handleQuarterChange(e.target.value)}
                    className={styles.quarterSelect}
                  >
                    <option value="">Select Quarter</option>
                    {Object.entries(quarters).map(([key, { label }]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            
            <div className={styles.sortOrderContainer}>
              <span className={styles.sortLabel}>Sort By:</span>
              <div className={styles.radioContainer}>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortOrder"
                    value="Recent"
                    checked={sortOrder === 'Recent'}
                    onChange={() => handleSortOrderChange('Recent')}
                  />
                  <span className={styles.radioText}>Recent</span>
                </label>
                <label className={styles.radioLabel}>
                  <input
                    type="radio"
                    name="sortOrder"
                    value="Oldest"
                    checked={sortOrder === 'Oldest'}
                    onChange={() => handleSortOrderChange('Oldest')}
                  />
                  <span className={styles.radioText}>Oldest</span>
                </label>
              </div>
            </div>

            <div className={styles.dateDisplayContainer}>
              {activeFilter === 'Custom Date' ? (
                <div className={styles.customDatePicker}>
                  <div className={styles.dateInputs}>
                    <label>
                      Start Date:
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </label>
                    <label>
                      End Date:
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              ) : (
                <div className={styles.dateDisplay}>
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>Start Date:</span>
                    <span className={styles.dateValue}>{startDate ? format(new Date(startDate), 'dd MMM yyyy') : 'Invalid Date'}</span>
                  </div>
                  <div className={styles.dateItem}>
                    <span className={styles.dateLabel}>End Date:</span>
                    <span className={styles.dateValue}>{endDate ? format(new Date(endDate), 'dd MMM yyyy') : 'Invalid Date'}</span>
                  </div>
                  {isTimeFilterActive && (
                    <div className={styles.timeFilterDisplay}>
                      <div className={styles.dateItem}>
                        <span className={styles.dateLabel}>Time:</span>
                        <span className={styles.dateValue}>{getTimeDisplayString()}</span>
                      </div>
                      <button className={styles.removeTimeFilter} onClick={removeTimeFilter}>
                        ✕ Remove Time Filter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {error && <p className={styles.billViewerError}>{error}</p>}

          {/* Time Range Modal */}
          {showTimeModal && (
            <div className={styles.timeModalOverlay}>
              <div className={styles.timeModalContent}>
                <h2>Select Time Range</h2>
                <button className={styles.timeModalClose} onClick={closeTimeModal}>✕</button>
                
                <div className={styles.timeInputsContainer}>
                  <div className={styles.timeInputGroup}>
                    <label>Start Time *</label>
                    <div className={styles.timeInputWrapper}>
                      <select
                        value={startTimeHour}
                        onChange={(e) => setStartTimeHour(e.target.value)}
                        className={styles.timeSelect}
                      >
                        <option value="">HH</option>
                        {hourOptions.map(hour => (
                          <option key={hour} value={hour}>{hour}</option>
                        ))}
                      </select>
                      <span className={styles.timeSeparator}>:</span>
                      <select
                        value={startTimeMinute}
                        onChange={(e) => setStartTimeMinute(e.target.value)}
                        className={styles.timeSelect}
                      >
                        {minuteOptions.map(minute => (
                          <option key={minute} value={minute}>{minute}</option>
                        ))}
                      </select>
                      <select
                        value={startTimePeriod}
                        onChange={(e) => setStartTimePeriod(e.target.value)}
                        className={styles.periodSelect}
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>

                  <div className={styles.timeInputGroup}>
                    <label>End Time (Optional)</label>
                    <div className={styles.timeInputWrapper}>
                      <select
                        value={endTimeHour}
                        onChange={(e) => setEndTimeHour(e.target.value)}
                        className={styles.timeSelect}
                      >
                        <option value="">HH</option>
                        {hourOptions.map(hour => (
                          <option key={hour} value={hour}>{hour}</option>
                        ))}
                      </select>
                      <span className={styles.timeSeparator}>:</span>
                      <select
                        value={endTimeMinute}
                        onChange={(e) => setEndTimeMinute(e.target.value)}
                        className={styles.timeSelect}
                      >
                        {minuteOptions.map(minute => (
                          <option key={minute} value={minute}>{minute}</option>
                        ))}
                      </select>
                      <select
                        value={endTimePeriod}
                        onChange={(e) => setEndTimePeriod(e.target.value)}
                        className={styles.periodSelect}
                      >
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className={styles.timeModalActions}>
                  <button className={styles.applyTimeButton} onClick={applyTimeFilter}>
                    Apply Time Filter
                  </button>
                  <button className={styles.cancelTimeButton} onClick={closeTimeModal}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {bills.length > 0 && (
            <div className={styles.billViewerTableContainer}>
              <table className={styles.billViewerTable}>
                <thead>
                  <tr>
                    <th>Bill No</th>
                    <th>Date</th>
                    <th>Time</th>
                    
                    <th>Customer Name</th>
                    <th>Customer Mobile</th>
                    <th>Bill Type</th>
                    <th>School</th>
                    <th>Amount</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.map((bill) => (
                    <tr key={bill.billNo}>
                      <td>{bill.billNo}</td>
                      <td>{format(new Date(bill.bill_date), 'dd MMM yyyy')}</td>
                      <td>
                          {bill.time
                          ? (() => {
                              const [h, m] = bill.time.split(":");
                              const hour = ((+h + 11) % 12 + 1);
                              const ampm = +h >= 12 ? "PM" : "AM";
                              return `${hour.toString().padStart(2, "0")}:${m} ${ampm}`;
                            })()
                          : "-"}
                      </td>

                      <td>{bill.customerName}</td>
                      <td>{bill.customerMobileNo}</td>
                      <td>{bill.billType}</td>
                      <td>{bill.schoolName}</td>
                      <td>{bill.final_amount}</td>
                      <td className={styles.actionButtons}>
                        <button onClick={() => handleViewDetails(bill)}>View</button>
                        <button onClick={() => handlePrintBill(bill.billNo)}>Print</button>

                        {bill.status === 'FRESH' && isToday(bill.bill_date) && (
                          <button onClick={() => openModal(bill.billNo, bill.final_amount)}>Update Transaction</button>
                        )}

                        {bill.status === 'FRESH' && isToday(bill.bill_date) && (
                          <button onClick={() => handleDeleteBill(bill.billNo)}>Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

{selectedBill && (
  <div className={styles.billDetailsModal}>
    <div className={styles.billDetailsModalContent}>
      <div className={styles.billDetailsHeader}>
        <h2 className={styles.billDetailsTitle}>Invoice Details</h2>
      </div>
      
      <div className={styles.billDetailsInfo}>
        <div className={styles.billDetailsInfoGrid}>
          <div className={styles.billDetailsInfoItem}>
            <span className={styles.billDetailsLabel}>Bill Number:</span>
            <span className={styles.billDetailsValue}>{selectedBill.billNo}</span>
          </div>
          <div className={styles.billDetailsInfoItem}>
            <span className={styles.billDetailsLabel}> Date:</span>
            <span className={styles.billDetailsValue}>
              {new Date(selectedBill.bill_date).toLocaleDateString('en-GB')}
            </span>
          </div>
          
          <div className={styles.billDetailsInfoItem}>
            <span className={styles.billDetailsLabel}> Time:</span>
            <span className={styles.billDetailsValue}>
                     {selectedBill.time
                      ? (() => {
                          const [h, m] = selectedBill.time.split(":");
                          const hour = ((+h + 11) % 12 + 1);
                          const ampm = +h >= 12 ? "PM" : "AM";
                          return `${hour.toString().padStart(2, "0")}:${m} ${ampm}`;
                        })()
                      : "-"}
            </span>
          </div>

          <div className={styles.billDetailsInfoItem}>
            <span className={styles.billDetailsLabel}>Customer Mobile:</span>
            <span className={styles.billDetailsValue}>{selectedBill.customerMobileNo}</span>
          </div>
        </div>
      </div>

      <div className={styles.billDetailsItemsSection}>
        <h3 className={styles.billDetailsItemsTitle}>Items Purchased</h3>
        <div className={styles.billDetailsTableContainer}>
          <div className={styles.billDetailsTableWrapper}>
            <table className={styles.billDetailsTable}>
              <thead>
                <tr>
                  <th>Item Type</th>
                  <th>School</th>
                  <th>Color</th>
                  <th>Size</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Discount %</th>
                  <th>Sell Price</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {selectedBill.bill.map((item, index) => (
                  <tr key={index}>
                    <td>{item.itemType}</td>
                    <td>{item.itemCategory}</td>
                    <td>{item.itemColor}</td>
                    <td>{item.itemSize}</td>
                    <td>{item.quantity}</td>
                    <td>₹{item.price}</td>
                    <td>{item.discount}%</td>
                    <td>₹{item.sellPrice}</td>
                    <td>₹{item.total_amount}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.billDetailsTotalRow}>
                  <td colSpan="8" className={styles.billDetailsTotalLabel}>
                    Total Amount:
                  </td>
                  <td className={styles.billDetailsTotalAmount}>
                    ₹{selectedBill.final_amount}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
      
      <div className={styles.billDetailsCloseContainer}>
        <button className={styles.billDetailsCloseBtn} onClick={closePopup}>
          Close
        </button>
      </div>
    </div>
  </div>
)}

          {pdfData && (
            <div className={styles.billViewPrintPdf}>
              <div className={styles.billViewPrintPdfContent}>
                <iframe
                  ref={pdfIframeRef}
                  src={pdfData}
                  title="Bill PDF"
                  width="100%"
                  height="500px"
                ></iframe>
                <div className={styles.billViewerPrintControls}>
                  <button onClick={printPdf}>Print</button>
                  <button onClick={closePdfModal}>Close</button>
                </div>
              </div>
            </div>
          )}

          {showTransactionModal && (
            <TransactionModal
              billNo={selectedBillNo}
              totalAmount={totalAmount}
              handleClose={closeModal}
            />
          )}

<PrintBillModal
  isOpen={showPrintModal}
  onClose={() => setShowPrintModal(false)}
  billInfo={billInfo}
/>


        </div>
      </div>
    </div>
  );

};

export default BillViewerPopUp;